import { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { translatedZodResolver } from '@/lib/utils/validation/translated-zod-resolver';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Textarea } from '@/components/ui-kit/textarea';
import { Label } from '@/components/ui-kit/label';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import { TimeInput } from '@/components/core/time-input/time-input';
import { RadioGroup, RadioGroupItem } from '@/components/ui-kit/radio-group';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { useToast } from '@/hooks/use-toast';
import { dateOnlyOf } from '@/lib/utils/custom-date/custom-date';
import { hrTermOn } from '@/lib/utils/work-days/work-days';
import { cn } from '@/lib/utils';
import {
  correctionFormSchema,
  CorrectionFormValues,
  filledBreakTimes,
} from '../../schemas/correction-form-schema';
import { useGetMyHrTerms, useSubmitAttendanceCorrection } from '../../hooks/use-attendance';
import { useAttendancePolicy } from '../../hooks/use-attendance-policy';
import {
  AttendancePolicy,
  attendanceDayStart,
  breakTypeOf,
  canTakeLunch,
  chargedBreakSeconds,
  correctionMinimumBlocker,
  correctionTimesOnDay,
  formatDuration,
  formatTimeOfDay,
  getOrgDateString,
  orgDayStart,
  RuleBlocker,
  TermWorkDay,
  toAttendanceDate,
  validateDayTotals,
  validateEnteredBreaks,
  visibleBreaks,
} from '../../services/attendance-rules';
import { BreakTimeDetail } from '../../types/attendance.types';

export interface CorrectionFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The day being corrected. Left out for a day with no record (a missed clock-in): the employee
   * then picks the date. The ATTENDANCE_CORRECTION_REQUEST workflow finds that day's record itself
   * and copies its department onto the correction. */
  correctionDate?: string;
  /** The day's already-recorded clock-in/out and break times (ISO datetime strings), used to
   * pre-fill the form so the user is correcting existing values rather than starting blank. */
  existingStartTime?: string;
  existingEndTime?: string;
  existingBreakTimes?: BreakTimeDetail[];
}

/** input[type=time] takes "HH:mm" on the organization's clock, as the attendance tables show it. */
const toTimeInputValue = (isoDateTime?: string): string =>
  isoDateTime ? formatTimeOfDay(isoDateTime) : '';

/** The form's "HH:mm" values as instants on `day`, a shift past midnight running into the next day. */
const toCorrectionTimes = (day: Date, values: CorrectionFormValues, withBreaks: boolean) =>
  correctionTimesOnDay(day, {
    StartTimeUtc: values.ClockStartTime,
    EndTimeUtc: values.ClockEndTime,
    BreakTimeDetails: withBreaks
      ? filledBreakTimes(values).map((entry) => ({
          StartTimeUtc: entry.Start,
          EndTimeUtc: entry.End,
          Type: entry.Type,
        }))
      : [],
  });

/** Any fixed day: the rules only compare the times relative to each other. */
const REFERENCE_DAY = new Date(Date.UTC(2000, 0, 1, 12));

/** FormLabel is unstyled (it wraps the raw Radix label): give its titles the ui-kit Label's look. */
const TITLE_CLASS = 'text-sm font-medium leading-none';

const COMPLETE_TIME = /^([0-1][0-9]|2[0-3]):([0-5][0-9])$/;

/** The charged break and working time of what is entered so far, or `null` until clock-in and
 * clock-out are complete; half-typed break rows are left out. */
const getCorrectionTotals = (
  policy: AttendancePolicy,
  values: CorrectionFormValues,
  withBreaks: boolean
): { breakSeconds: number; workSeconds: number } | null => {
  if (!COMPLETE_TIME.test(values.ClockStartTime) || !COMPLETE_TIME.test(values.ClockEndTime)) {
    return null;
  }
  if (values.ClockStartTime === values.ClockEndTime) return null;
  const completeBreaks = (values.BreakTimes ?? []).filter(
    (entry) => COMPLETE_TIME.test(entry.Start) && COMPLETE_TIME.test(entry.End)
  );
  const times = toCorrectionTimes(
    REFERENCE_DAY,
    { ...values, BreakTimes: completeBreaks },
    withBreaks
  );
  const clockSeconds =
    (new Date(times.EndTimeUtc).getTime() - new Date(times.StartTimeUtc).getTime()) / 1000;
  const breakSeconds = chargedBreakSeconds(policy, times.BreakTimeDetails);
  return { breakSeconds, workSeconds: Math.max(0, clockSeconds - breakSeconds) };
};

/**
 * The correction's rule failure, or `null`. Every rule is one the employee's permissions switch on
 * through the rule catalog: the full/half-day minimum of the day's hours in the HR term
 * (correctionMinHours, read from `workDays` on `day`),
 * the per-break rules (minBreakLength, plus start-before-end, inside the day and no overlap for anyone
 * entering breaks), the minimum total break tiers and the maximum work time.
 */
const getCorrectionRuleError = (
  policy: AttendancePolicy,
  values: CorrectionFormValues,
  withBreaks: boolean,
  keepsDayBreaks: boolean,
  workDays: readonly TermWorkDay[] | null | undefined,
  day: string
): RuleBlocker | null => {
  const times = toCorrectionTimes(REFERENCE_DAY, values, withBreaks);
  const clockIn = new Date(times.StartTimeUtc);
  const clockOut = new Date(times.EndTimeUtc);
  const clockSeconds = (clockOut.getTime() - clockIn.getTime()) / 1000;

  const minimum = correctionMinimumBlocker(
    policy,
    clockSeconds,
    values.LeaveRequestType,
    workDays,
    day
  );
  if (minimum) return minimum;

  const breaks = times.BreakTimeDetails;
  const invalidBreak = validateEnteredBreaks(policy, breaks, clockIn, clockOut);
  if (invalidBreak) return { key: invalidBreak };
  // Without break rows the workflow keeps the day's own breaks inside the new times, which this form
  // doesn't know exactly: the break totals are left to it then.
  if (breaks.length === 0 && keepsDayBreaks) return null;

  const charged = chargedBreakSeconds(policy, breaks);
  const totalsError = validateDayTotals(policy, clockSeconds - charged, charged, true);
  return totalsError ? { key: totalsError } : null;
};

export const CorrectionFormDialog = ({
  open,
  onOpenChange,
  correctionDate,
  existingStartTime,
  existingEndTime,
  existingBreakTimes,
}: CorrectionFormDialogProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  // The permissions that unlock breaks on the clock widget also unlock break entries plus a
  // comment here; the correctionMinHours rule adds the full/half-day choice. No country check.
  const policy = useAttendancePolicy();
  const isBreakVariant = policy.canTakeBreak || canTakeLunch(policy);
  const showDayType = Boolean(policy.rules.correctionMinHours);
  // The minimum is a share of the corrected day's hours in the HR term.
  const { data: hrTerms, isLoading: isHrTermsLoading } = useGetMyHrTerms();

  const { mutateAsync: submitCorrection, isPending } = useSubmitAttendanceCorrection();
  const [pickedDate, setPickedDate] = useState('');
  const [dateError, setDateError] = useState(false);
  const today = getOrgDateString(new Date());

  const initialFormValues = useMemo<CorrectionFormValues>(() => {
    // The gaps between sessions of a day are not breaks: the correction replaces the day as one session.
    const existingBreaks = visibleBreaks(existingBreakTimes ?? [])
      .map((breakTime) => ({
        Start: toTimeInputValue(breakTime.StartTimeUtc),
        End: toTimeInputValue(breakTime.EndTimeUtc),
        Type: breakTypeOf(breakTime) === 'Lunch' ? ('Lunch' as const) : ('Regular' as const),
      }))
      .filter((breakTime) => breakTime.Start && breakTime.End);
    return {
      LeaveRequestType: 'fullday',
      ClockStartTime: toTimeInputValue(existingStartTime),
      ClockEndTime: toTimeInputValue(existingEndTime),
      Comment: '',
      BreakTimes: isBreakVariant ? existingBreaks : [],
    };
  }, [existingStartTime, existingEndTime, existingBreakTimes, isBreakVariant]);

  const form = useForm<CorrectionFormValues>({
    resolver: translatedZodResolver(correctionFormSchema, t),
    defaultValues: initialFormValues,
  });

  useEffect(() => {
    if (!open) return;
    form.reset(initialFormValues);
    setPickedDate('');
    setDateError(false);
  }, [open, initialFormValues, form]);

  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'BreakTimes' });
  const totals = getCorrectionTotals(policy, form.watch(), isBreakVariant);

  const handleSubmit = async (values: CorrectionFormValues) => {
    if (!correctionDate && !pickedDate) {
      setDateError(true);
      return;
    }
    const dayText = correctionDate ? dateOnlyOf(correctionDate) : pickedDate;
    const ruleError = getCorrectionRuleError(
      policy,
      values,
      isBreakVariant,
      visibleBreaks(existingBreakTimes ?? []).length > 0,
      hrTermOn(hrTerms, dayText)?.WorkDays,
      dayText
    );
    if (ruleError) {
      toast({
        title: t(
          ruleError.key.endsWith('_REQUIRES_MIN_HOURS')
            ? 'MINIMUM_REQUEST_HOURS_NOT_MET'
            : 'INVALID_CORRECTION_REQUEST'
        ),
        description: t(ruleError.key, ruleError.params ?? {}),
        variant: 'destructive',
      });
      return;
    }

    // The form collects "HH:mm" on the organization's clock; the correction stores instants on its day.
    const day = correctionDate ? attendanceDayStart(correctionDate) : orgDayStart(pickedDate);
    const times = toCorrectionTimes(day, values, isBreakVariant);
    if (new Date(times.EndTimeUtc).getTime() > Date.now()) {
      toast({
        title: t('INVALID_CORRECTION_REQUEST'),
        description: t('ATTENDANCE_CORRECTION_IN_FUTURE'),
        variant: 'destructive',
      });
      return;
    }

    try {
      await submitCorrection({
        // The corrected day as its UTC midnight, the same shape as `AttendanceDate`.
        CorrectionDate: correctionDate ?? toAttendanceDate(pickedDate),
        StartTimeUtc: times.StartTimeUtc,
        EndTimeUtc: times.EndTimeUtc,
        Comment: isBreakVariant ? values.Comment : undefined,
        BreakTimeDetails: times.BreakTimeDetails,
        // correctionMinHours rule: the workflow re-checks the minimum for this choice.
        ...(showDayType ? { DayType: values.LeaveRequestType } : {}),
      });
      onOpenChange(false);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" alignCloseWithTitle>
        <DialogHeader className="mb-3">
          <DialogTitle>{t('REQUEST_ATTENDANCE_CORRECTION')}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
            {!correctionDate && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <Label>{t('CORRECTION_DATE')}:</Label>
                <div className="min-w-[12rem] flex-1">
                  <DatePicker
                    value={pickedDate}
                    onChange={(value) => {
                      setPickedDate(value);
                      setDateError(false);
                    }}
                    max={today}
                  />
                </div>
                {dateError && (
                  <p className="basis-full text-sm font-medium text-destructive">
                    {t('CORRECTION_DATE_REQUIRED')}
                  </p>
                )}
              </div>
            )}

            {/* The correction type depends on the HR term's hours: a placeholder until they load
                (Submit stays disabled meanwhile). */}
            {showDayType && isHrTermsLoading && (
              <div className="flex items-center gap-4" aria-busy="true">
                <Skeleton className="h-5 w-28" />
                <Skeleton className="h-5 w-24" />
                <Skeleton className="h-5 w-24" />
              </div>
            )}

            {showDayType && !isHrTermsLoading && (
              <FormField
                control={form.control}
                name="LeaveRequestType"
                render={({ field }) => (
                  // Tighter gaps on a phone keep the options on the title's line.
                  <FormItem className="flex flex-wrap items-center gap-x-4 gap-y-2 space-y-0 max-sm:gap-x-3">
                    <FormLabel className={cn(TITLE_CLASS, 'whitespace-nowrap')}>
                      {t('CORRECTION_TYPE')}:
                    </FormLabel>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        className="flex flex-wrap gap-x-4 gap-y-2 max-sm:gap-x-3"
                      >
                        <label className="flex items-center gap-2 whitespace-nowrap text-sm max-sm:gap-1.5">
                          <RadioGroupItem value="fullday" />
                          {t('FULL_DAY')}
                        </label>
                        <label className="flex items-center gap-2 whitespace-nowrap text-sm max-sm:gap-1.5">
                          <RadioGroupItem value="halfday" />
                          {t('HALF_DAY')}
                        </label>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage className="basis-full" />
                  </FormItem>
                )}
              />
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="ClockStartTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={TITLE_CLASS}>
                      {t('CLOCK_START_TIME')}
                      <span className="ml-1 text-xs font-normal text-muted-foreground">
                        ({t('24_HOURS_FORMAT')})
                      </span>
                    </FormLabel>
                    <FormControl>
                      <TimeInput {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="ClockEndTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={TITLE_CLASS}>
                      {t('CLOCK_END_TIME')}
                      <span className="ml-1 text-xs font-normal text-muted-foreground">
                        ({t('24_HOURS_FORMAT')})
                      </span>
                    </FormLabel>
                    <FormControl>
                      <TimeInput {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {isBreakVariant && (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-medium">
                    {t('BREAKS')}
                    <span className="ml-1 text-xs font-normal text-muted-foreground">
                      ({t('24_HOURS_FORMAT')})
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => append({ Start: '', End: '', Type: 'Regular' })}
                  >
                    <Plus />
                    {t('ADD_BREAK')}
                  </Button>
                </div>
                {fields.map((fieldItem, index) => (
                  <div key={fieldItem.id} className="flex flex-wrap items-end gap-2">
                    <FormField
                      control={form.control}
                      name={`BreakTimes.${index}.Start`}
                      render={({ field }) => (
                        <FormItem className="min-w-[7.5rem] flex-1">
                          <FormControl>
                            <TimeInput aria-label={t('BREAK_START_TIME')} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`BreakTimes.${index}.End`}
                      render={({ field }) => (
                        <FormItem className="min-w-[7.5rem] flex-1">
                          <FormControl>
                            <TimeInput aria-label={t('BREAK_END_TIME')} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full min-[420px]:w-auto"
                      onClick={() => remove(index)}
                    >
                      {t('REMOVE')}
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {isBreakVariant && (
              <FormField
                control={form.control}
                name="Comment"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={TITLE_CLASS}>{t('COMMENT')}</FormLabel>
                    <FormControl>
                      <Textarea rows={3} placeholder={t('COMMENT')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {isBreakVariant && (
                <div className="flex items-center gap-2">
                  <Label>{t('TOTAL_BREAK_TIME')}:</Label>
                  <span className="text-sm">
                    {totals ? formatDuration(totals.breakSeconds) : '--:--'}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Label>{t('TOTAL_WORKING_HOURS')}:</Label>
                <span className="text-sm">
                  {totals ? formatDuration(totals.workSeconds) : '--:--'}
                </span>
              </div>
            </div>

            <DialogFooter>
              <DialogCancelButton onClick={() => onOpenChange(false)}>
                {t('CANCEL')}
              </DialogCancelButton>
              <Button
                type="submit"
                loading={isPending}
                disabled={isPending || (showDayType && isHrTermsLoading)}
              >
                {t('SUBMIT')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
